import { useState, useCallback } from 'react';
import { battlesApi } from '../../../api/battles';

/**
 * Хук для управления регламентом баттла
 */
export const useBattleRegulation = (battleId, fetchBattle) => {
  const [isRegulationModalOpen, setIsRegulationModalOpen] = useState(false);
  const [regulationActionLoading, setRegulationActionLoading] = useState(false);

  const [regulationForm, setRegulationForm] = useState({
    rules: [],
    limitations: [],
    ageLimits: '',
    formats: null,
  });

  /** Сохраняем исходные правила/ограничения для diff при сохранении */
  const [originalRules, setOriginalRules] = useState([]);
  const [originalLimitations, setOriginalLimitations] = useState([]);

  /** Открыть модалку регламента */
  const openRegulationModal = useCallback((regulation) => {
    const mappedRules =
        regulation?.rules?.map((r) => ({
          id: typeof r !== 'string' ? r.id : null,
          title: typeof r === 'string' ? r : r.title,
          description: typeof r !== 'string' ? r.description : '',
        })) || [];
    const mappedLimitations =
        regulation?.limitations?.map((l) => ({
          id: typeof l !== 'string' ? l.id : null,
          title: typeof l === 'string' ? l : l.title,
          description: typeof l !== 'string' ? l.description : '',
          value: typeof l !== 'string' ? l.value : '',
        })) || [];

    setOriginalRules(mappedRules);
    setOriginalLimitations(mappedLimitations);
    setRegulationForm({
      rules: mappedRules,
      limitations: mappedLimitations,
      ageLimits: regulation?.ageLimits || '',
      formats: regulation?.formats || null,
    });
    setIsRegulationModalOpen(true);
  }, []);

  /** Закрыть модалку */
  const closeRegulationModal = useCallback(() => {
    setIsRegulationModalOpen(false);
  }, []);

  /** Изменение поля формы */
  const handleRegulationFormChange = useCallback((field, value) => {
    setRegulationForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  /** Добавить правило */
  const handleAddRuleField = useCallback(() => {
    setRegulationForm((prev) => ({
      ...prev,
      rules: [...prev.rules, { title: '', description: '' }],
    }));
  }, []);

  /** Обновить правило */
  const handleUpdateRuleField = useCallback((index, field, value) => {
    setRegulationForm((prev) => ({
      ...prev,
      rules: prev.rules.map((r, i) =>
        i === index ? { ...r, [field]: value } : r
      ),
    }));
  }, []);

  /** Удалить правило */
  const handleRemoveRuleField = useCallback((index) => {
    setRegulationForm((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    }));
  }, []);

  /** Добавить ограничение */
  const handleAddLimitationField = useCallback(() => {
    setRegulationForm((prev) => ({
      ...prev,
      limitations: [...prev.limitations, { id: null, title: '', description: '', value: '' }],
    }));
  }, []);

  /** Обновить ограничение */
  const handleUpdateLimitationField = useCallback((index, field, value) => {
    setRegulationForm((prev) => ({
      ...prev,
      limitations: prev.limitations.map((l, i) =>
        i === index ? { ...l, [field]: value } : l
      ),
    }));
  }, []);

  /** Удалить ограничение */
  const handleRemoveLimitationField = useCallback((index) => {
    setRegulationForm((prev) => ({
      ...prev,
      limitations: prev.limitations.filter((_, i) => i !== index),
    }));
  }, []);

  /** Обновить регламент */
  const handleUpdateRegulation = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        setRegulationActionLoading(true);

        // 1. Обновляем поля регламента (ageLimits, formats)
        await battlesApi.updateRegulation(battleId, {
          ageLimits: regulationForm.ageLimits?.trim() || null,
          formats: regulationForm.formats?.trim() || null,
        });

        // 2. Синхронизируем правила: создаём новые, обновляем изменённые, удаляем удалённые
        const formRuleIds = new Set(regulationForm.rules.filter(r => r.id).map(r => r.id));
        const rulesToCreate = regulationForm.rules.filter(r => !r.id && r.title.trim());
        const rulesToUpdate = regulationForm.rules.filter(r => r.id && r.title.trim());
        const rulesToDelete = originalRules.filter(r => r.id && !formRuleIds.has(r.id));

        for (const rule of rulesToDelete) {
          await battlesApi.deleteRule(battleId, rule.id);
        }
        for (const rule of rulesToCreate) {
          await battlesApi.createRule(battleId, {
            title: rule.title.trim(),
            description: rule.description?.trim() || null,
          });
        }
        for (const rule of rulesToUpdate) {
          await battlesApi.updateRule(battleId, rule.id, {
            title: rule.title.trim(),
            description: rule.description?.trim() || null,
          });
        }

        // 3. Синхронизируем ограничения
        const formLimIds = new Set(regulationForm.limitations.filter(l => l.id).map(l => l.id));
        const limsToCreate = regulationForm.limitations.filter(l => !l.id && l.title.trim());
        const limsToUpdate = regulationForm.limitations.filter(l => l.id && l.title.trim());
        const limsToDelete = originalLimitations.filter(l => l.id && !formLimIds.has(l.id));

        for (const lim of limsToDelete) {
          await battlesApi.deleteLimitation(battleId, lim.id);
        }
        for (const lim of limsToCreate) {
          await battlesApi.createLimitation(battleId, {
            title: lim.title.trim(),
            description: lim.description?.trim() || null,
            value: lim.value?.trim() || null,
          });
        }
        for (const lim of limsToUpdate) {
          await battlesApi.updateLimitation(battleId, lim.id, {
            title: lim.title.trim(),
            description: lim.description?.trim() || null,
            value: lim.value?.trim() || null,
          });
        }

        await fetchBattle();
        closeRegulationModal();
      } catch (err) {
        console.error(
          '[useBattleRegulation] Failed to update regulation:',
          err.message
        );
        throw err;
      } finally {
        setRegulationActionLoading(false);
      }
    },
    [battleId, regulationForm, originalRules, originalLimitations, fetchBattle, closeRegulationModal]
  );

  return {
    // State
    isRegulationModalOpen,
    regulationActionLoading,
    regulationForm,

    // Setters
    setRegulationForm,

    // Actions
    openRegulationModal,
    closeRegulationModal,
    handleRegulationFormChange,
    handleAddRuleField,
    handleUpdateRuleField,
    handleRemoveRuleField,
    handleAddLimitationField,
    handleUpdateLimitationField,
    handleRemoveLimitationField,
    handleUpdateRegulation,
  };
};
